import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Eye, Megaphone, MousePointerClick } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/StatCard';
import { CampaignStatusBadge } from '@/components/StatusBadge';
import { EmptyState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { portalApi } from '@/lib/api';
import { queryKeys } from '@/lib/queries';
import { daysAgoUtc, formatCount, formatPercent, todayUtc } from '@/lib/utils';

export const Route = createFileRoute('/portal/')({ component: PortalOverview });

const WINDOW_DAYS = 30;

function PortalOverview() {
  const from = daysAgoUtc(WINDOW_DAYS - 1);
  const to = todayUtc();

  const campaignsQuery = useQuery({
    queryKey: queryKeys.portalCampaigns,
    queryFn: portalApi.campaigns,
  });
  const campaigns = campaignsQuery.data?.campaigns ?? [];
  const active = campaigns.filter((c) => c.status === 'active');

  const reporting = useQuery({
    // Blended, matching the admin overview — the split lives on /portal/reporting.
    queryKey: queryKeys.portalReporting(from, to, undefined, 'all'),
    queryFn: () => portalApi.reporting(from, to),
  });
  const totals = reporting.data?.totals;

  return (
    <>
      <PageHeader
        title={campaignsQuery.data?.sponsor.name ?? 'Overview'}
        description={`Delivery across your campaigns · last ${WINDOW_DAYS} days`}
      />
      <PageBody>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <StatCard
            label="Active campaigns"
            value={formatCount(active.length)}
            hint={`${campaigns.length} total`}
            icon={Megaphone}
            loading={campaignsQuery.isPending}
          />
          <StatCard
            label="Impressions"
            value={formatCount(totals?.impressions ?? 0)}
            icon={Eye}
            loading={reporting.isPending}
          />
          <StatCard
            label="Click-through rate"
            value={formatPercent(reporting.data?.ctr)}
            hint={`${formatCount(totals?.clicks ?? 0)} clicks`}
            icon={MousePointerClick}
            loading={reporting.isPending}
          />
        </div>

        <Card className="shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Campaigns</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {campaigns.length === 0 && !campaignsQuery.isPending ? (
              <EmptyState
                title="No campaigns yet"
                description="Your campaigns will appear here once DearDoc sets one up."
                icon={Megaphone}
              />
            ) : (
              <div className="divide-y">
                {campaigns.map((campaign) => (
                  <div key={campaign.id} className="flex items-center justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{campaign.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatCount(campaign.impressionsDelivered)} impressions delivered
                      </p>
                    </div>
                    <CampaignStatusBadge status={campaign.status} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button asChild variant="outline" size="sm">
            <Link to="/portal/reporting">View full reporting</Link>
          </Button>
        </div>
      </PageBody>
    </>
  );
}
